import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants';
import { LayoutWrapper } from '../components/LayoutWrapper';

const defaultMessages = defineMessages({
  'faq.title': { id: 'faq.title', defaultMessage: 'Часто задаваемые вопросы' },
  'faq.subtitle': {
    id: 'faq.subtitle',
    defaultMessage:
      'Найдите ответы на самые популярные вопросы о работе с NovoService',
  },
  'faq.noAnswerTitle': {
    id: 'faq.noAnswerTitle',
    defaultMessage: 'Не нашли ответ на свой вопрос?',
  },
  'faq.noAnswerText': {
    id: 'faq.noAnswerText',
    defaultMessage: 'Наша служба поддержки всегда готова помочь вам',
  },
  'faq.contactButton': {
    id: 'faq.contactButton',
    defaultMessage: 'Связаться с поддержкой',
  },
  'faq.category.0': { id: 'faq.category.0', defaultMessage: 'Общие вопросы' },
  'faq.category.1': { id: 'faq.category.1', defaultMessage: 'Для клиентов' },
  'faq.category.2': {
    id: 'faq.category.2',
    defaultMessage: 'Для специалистов',
  },
  'faq.question.1': {
    id: 'faq.question.1',
    defaultMessage: 'Что такое NovoService?',
  },
  'faq.answer.1': {
    id: 'faq.answer.1',
    defaultMessage:
      'NovoService - это онлайн-платформа, которая соединяет поставщиков услуг с клиентами в Румынии. Мы помогаем найти проверенных специалистов для различных задач: от ремонта до образовательных услуг.',
  },
  'faq.question.2': {
    id: 'faq.question.2',
    defaultMessage: 'Как начать пользоваться сервисом?',
  },
  'faq.answer.2': {
    id: 'faq.answer.2',
    defaultMessage:
      'Чтобы начать пользоваться сервисом, просто зарегистрируйтесь на платформе. Вы можете искать услуги без регистрации, но для размещения своих предложений необходимо создать аккаунт.',
  },
  'faq.question.3': {
    id: 'faq.question.3',
    defaultMessage: 'Как найти подходящего специалиста?',
  },
  'faq.answer.3': {
    id: 'faq.answer.3',
    defaultMessage:
      'Используйте поисковую систему и фильтры на нашем сайте. Вы можете искать по категории услуг, местоположению и цене.',
  },
  'faq.question.5': {
    id: 'faq.question.5',
    defaultMessage: 'Как разместить объявление об услуге?',
  },
  'faq.answer.5': {
    id: 'faq.answer.5',
    defaultMessage:
      "Чтобы разместить объявление, войдите в свой аккаунт и нажмите 'Разместить услугу'. Заполните всю необходимую информацию о вашей услуге, включая описание, цены и фотографии. После модерации ваше объявление будет опубликовано.",
  },
});

const faqData = [
  {
    category: <FormattedMessage {...defaultMessages['faq.category.0']} />,
    questions: [
      {
        id: 1,
        question: <FormattedMessage {...defaultMessages['faq.question.1']} />,
        answer: <FormattedMessage {...defaultMessages['faq.answer.1']} />,
      },
      {
        id: 2,
        question: <FormattedMessage {...defaultMessages['faq.question.2']} />,
        answer: <FormattedMessage {...defaultMessages['faq.answer.2']} />,
      },
    ],
  },
  {
    category: <FormattedMessage {...defaultMessages['faq.category.1']} />,
    questions: [
      {
        id: 3,
        question: <FormattedMessage {...defaultMessages['faq.question.3']} />,
        answer: <FormattedMessage {...defaultMessages['faq.answer.3']} />,
      },
    ],
  },
  {
    category: <FormattedMessage {...defaultMessages['faq.category.2']} />,
    questions: [
      {
        id: 5,
        question: <FormattedMessage {...defaultMessages['faq.question.5']} />,
        answer: <FormattedMessage {...defaultMessages['faq.answer.5']} />,
      },
    ],
  },
];

function AccordionItem({ question, answer, isOpen, onClick }) {
  return (
    <div className='border-b border-gray-200 last:border-0'>
      <button
        className='w-full p-4 flex justify-between items-center text-left'
        onClick={onClick}
      >
        <span className='font-medium text-gray-900'>{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 px-4 ${
          isOpen ? 'max-h-96 pb-4' : 'max-h-0'
        }`}
      >
        <p className='text-gray-600'>{answer}</p>
      </div>
    </div>
  );
}

export function FAQ() {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (id) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <LayoutWrapper hasHero>
      <div className='bg-blue-600 text-white py-16'>
        <div className='container mx-auto px-4 text-center'>
          <HelpCircle className='w-16 h-16 mx-auto mb-6' />
          <h1 className='text-4xl font-bold mb-4'>
            <FormattedMessage {...defaultMessages['faq.title']} />
          </h1>
          <p className='text-xl text-blue-100 max-w-2xl mx-auto'>
            <FormattedMessage {...defaultMessages['faq.subtitle']} />
          </p>
        </div>
      </div>

      <div className='container mx-auto px-4 py-12'>
        <div className='max-w-3xl mx-auto'>
          {faqData.map((category, index) => (
            <div key={index} className='mb-8 last:mb-0'>
              <h2 className='text-2xl font-bold mb-6'>{category.category}</h2>
              <div className='bg-white rounded-xl shadow-sm overflow-hidden'>
                {category.questions.map(({ id, question, answer }) => (
                  <AccordionItem
                    key={id}
                    question={question}
                    answer={answer}
                    isOpen={openItems.includes(id)}
                    onClick={() => toggleItem(id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className='max-w-3xl mx-auto mt-12 text-center'>
          <h2 className='text-2xl font-bold mb-4'>
            <FormattedMessage {...defaultMessages['faq.noAnswerTitle']} />
          </h2>
          <p className='text-gray-600 mb-6'>
            <FormattedMessage {...defaultMessages['faq.noAnswerText']} />
          </p>
          <Link to={ROUTES.contact}>
            <button className='bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700'>
              <FormattedMessage {...defaultMessages['faq.contactButton']} />
            </button>
          </Link>
        </div>
      </div>
    </LayoutWrapper>
  );
}
